import { db } from '../../core/firebase/admin.js'
import { calculateDistance } from '@my-roadtrip/utils'
import { createLogger } from '../../common/utils/logger.js'
import { toIsoDate, toNumber, coerceNumber, firstNumber, normalizePlanInfo as sharedNormalizePlanInfo, normalizeUserStatus as sharedNormalizeStatus } from '../../common/utils/normalize.js'
import type {
  AdminDashboardStats,
  AdminUserListItem,
  AdminUserDetail,
  ModerationFlag,
  ModerationStats,
  AuditLogEntry,
  UserStatus,
  UserRole,
  AdminUserPlanInfo,
  LatLng,
  AdminGamificationSummary,
  AdminGamificationPlayerSummary,
  AdminGamificationBadgeSummary,
  AdminKeywordStat
} from '@my-roadtrip/types'
import type { Plan } from '@my-roadtrip/types'
import type { ListUsersQuery } from './admin.schemas.js'
import type { FirestoreDoc, FirestoreSnapshot, FirestoreQuery } from '../../common/types/firestore.js'
import { isString, isNonEmptyString } from '../../common/utils/type-guards.js'

/* ---------------------------------- utils --------------------------------- */

const logger = createLogger('AdminRepository')

const STOP_WORDS = new Set(
  [
    'the', 'and', 'for', 'les', 'des', 'une', 'avec', 'pour', 'dans', 'from', 'avec',
    'chez', 'vers', 'your', 'vous', 'notre', 'nos', 'leur', 'leurs', 'sur', 'de',
    'la', 'le', 'du', 'un', 'une', 'en', 'au', 'aux', 'par', 'que', 'qui', 'plus',
    'plan', 'trip', 'roadtrip', 'route', 'day', 'jour', 'jours', 'ride'
  ].map(word => word.toLowerCase())
)

type KeywordCountMap = Map<string, number>

class KeywordCollector {
  private readonly counts: KeywordCountMap = new Map()
  totalTokens = 0

  addText(text: unknown, weight = 1): void {
    if (!isNonEmptyString(text)) return
    const normalized = text
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()
      .replace(/[^a-z0-9\s-]+/g, ' ')
    const tokens = normalized.split(/\s+/).filter(Boolean)
    for (const token of tokens) {
      const cleaned = token.replace(/^-+|-+$/g, '')
      if (cleaned.length < 3 || STOP_WORDS.has(cleaned)) continue
      this.totalTokens += weight
      this.counts.set(cleaned, (this.counts.get(cleaned) ?? 0) + weight)
    }
  }

  addTerms(terms: string[], weight = 1): void {
    for (const term of terms) {
      this.addText(term, weight)
    }
  }

  top(limit: number, totalBaseline = this.totalTokens): AdminKeywordStat[] {
    return Array.from(this.counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit)
      .map(([label, count]) => ({
        label,
        count,
        trend: totalBaseline > 0 ? Math.round((count / totalBaseline) * 100) : 0
      }))
  }

  uniqueCount(): number {
    return this.counts.size
  }
}

function extractStringArray(value: unknown, limit = 16): string[] {
  if (!Array.isArray(value)) return []
  const normalized = (value as unknown[])
    .map(entry => {
      if (isString(entry)) return entry.trim()
      if (entry == null) return ''
      return String(entry).trim()
    })
    .filter(Boolean)
  return normalized.slice(0, limit)
}

type SearchLogEntry = { query: string; createdAt: string; tags: string[] }

interface NormalizedPoi {
  category: string
  city?: string | null
  createdAt?: string | null
}

class PoiStatsTracker {
  total = 0
  savedToday = 0
  readonly categories: Map<string, number> = new Map()
  readonly cities: Map<string, number> = new Map()
  private readonly perPlanCounts: Map<string, number> = new Map()

  add(planId: string | null, poi: NormalizedPoi, startOfDay: number): void {
    if (!poi.category) return
    this.total += 1
    this.categories.set(poi.category, (this.categories.get(poi.category) ?? 0) + 1)
    if (poi.city) {
      this.cities.set(poi.city, (this.cities.get(poi.city) ?? 0) + 1)
    }
    if (planId) {
      this.perPlanCounts.set(planId, (this.perPlanCounts.get(planId) ?? 0) + 1)
    }
    if (poi.createdAt) {
      const timestamp = new Date(poi.createdAt).getTime()
      if (!Number.isNaN(timestamp) && timestamp >= startOfDay) {
        this.savedToday += 1
      }
    }
  }

  averagePerPlan(totalPlans: number): number {
    if (totalPlans <= 0) return 0
    return Math.round((this.total / totalPlans) * 10) / 10
  }
}

function normalizePoiRecord(raw: unknown): NormalizedPoi | null {
  if (!raw || typeof raw !== 'object') return null
  const data = raw as Record<string, unknown>
  const category =
    typeof data.category === 'string' && data.category.trim().length > 0
      ? data.category.trim()
      : 'Divers'

  const createdAt = toIsoDate(
    data.createdAt ??
    data.updatedAt ??
    (data as { timestamp?: unknown }).timestamp ??
    (data as { addedAt?: unknown }).addedAt ??
    null
  )

  const locationRaw =
    typeof data.location === 'object' && data.location !== null
      ? (data.location as Record<string, unknown>)
      : undefined

  const address =
    typeof data.address === 'string'
      ? data.address
      : typeof locationRaw?.address === 'string'
        ? locationRaw.address
        : typeof locationRaw?.name === 'string'
          ? locationRaw.name
          : undefined

  const city = inferPoiCity(address)

  return {
    category,
    city,
    createdAt
  }
}

function inferPoiCity(address?: string): string | null {
  if (!address) return null
  const parts = address.split(',').map(part => part.trim()).filter(Boolean)
  if (parts.length === 0) return null
  if (parts.length === 1) {
    const subParts = parts[0].split('-').map(part => part.trim()).filter(Boolean)
    return subParts.length ? capitalize(subParts[subParts.length - 1]) : capitalize(parts[0])
  }
  return capitalize(parts[parts.length - 2] || parts[0])
}

function capitalize(value: string): string {
  if (!value) return value
  return value.charAt(0).toUpperCase() + value.slice(1)
}

function getNestedValue(source: unknown, path: string): unknown {
  if (!source || typeof source !== 'object') return undefined
  return path.split('.').reduce<unknown>((acc, key) => {
    if (!acc || typeof acc !== 'object') return undefined
    return (acc as Record<string, unknown>)[key]
  }, source)
}

function collectPlanKeywords(
  collector: KeywordCollector,
  tagCounts: Map<string, number>,
  data: Record<string, unknown>
): boolean {
  const before = collector.totalTokens
  const name =
    typeof data.name === 'string'
      ? data.name
      : typeof (data as { planName?: unknown }).planName === 'string'
        ? ((data as { planName: string }).planName)
        : null
  if (name) collector.addText(name, 2)

  const description =
    typeof data.description === 'string'
      ? data.description
      : typeof (data as { planDescription?: unknown }).planDescription === 'string'
        ? ((data as { planDescription: string }).planDescription)
        : null
  if (description) collector.addText(description)

  const metadata =
    typeof data.metadata === 'object' && data.metadata !== null
      ? (data.metadata as Record<string, unknown>)
      : undefined

  const tags = [
    ...extractStringArray(data.tags, 24),
    ...extractStringArray(metadata?.tags, 24),
    ...extractStringArray(metadata?.themes, 24)
  ]
  if (tags.length) {
    collector.addTerms(tags, 2)
    for (const tag of tags) {
      tagCounts.set(tag, (tagCounts.get(tag) ?? 0) + 1)
    }
  }

  const aiMetadata =
    typeof data.aiMetadata === 'object' && data.aiMetadata !== null
      ? (data.aiMetadata as Record<string, unknown>)
      : undefined
  const promptSummary =
    typeof aiMetadata?.promptSummary === 'string' ? (aiMetadata.promptSummary as string) : null
  if (promptSummary) collector.addText(promptSummary)

  const notes =
    typeof (data as { summary?: unknown }).summary === 'string'
      ? ((data as { summary: string }).summary)
      : typeof (data as { notes?: unknown }).notes === 'string'
        ? ((data as { notes: string }).notes)
        : null
  if (notes) collector.addText(notes)

  return collector.totalTokens > before
}

function collectStageKeywords(collector: KeywordCollector, data: Record<string, unknown>): void {
  if (typeof data.name === 'string') {
    collector.addText(data.name)
  }
  if (typeof data.notes === 'string') {
    collector.addText(data.notes)
  }
  const activities = Array.isArray((data as { activities?: unknown[] }).activities)
    ? ((data as { activities: unknown[] }).activities)
    : []
  for (const activity of activities) {
    if (activity && typeof activity === 'object') {
      const activityData = activity as Record<string, unknown>
      const label =
        typeof activityData.name === 'string'
          ? activityData.name
          : typeof activityData.title === 'string'
            ? activityData.title
            : null
      if (label) {
        collector.addText(label)
      }
    }
  }
}

function mapToStats(
  map: Map<string, number>,
  totalBaseline: number,
  limit = 6
): AdminKeywordStat[] {
  if (map.size === 0) return []
  return Array.from(map.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([label, count]) => ({
      label,
      count,
      trend: totalBaseline > 0 ? Math.round((count / totalBaseline) * 100) : 0
    }))
}

function extractPois(raw: unknown, fallbackTimestamp?: string | null): NormalizedPoi[] {
  if (!Array.isArray(raw)) return []
  return (raw as unknown[])
    .map(entry => {
      const normalized = normalizePoiRecord(entry)
      if (!normalized) return null
      if (!normalized.createdAt && fallbackTimestamp) {
        normalized.createdAt = fallbackTimestamp
      }
      return normalized
    })
    .filter(Boolean) as NormalizedPoi[]
}

function extractStageCoordinates(data: Record<string, unknown>): LatLng | null {
  const locationRaw = data.location
  const location =
    locationRaw && typeof locationRaw === 'object'
      ? (locationRaw as Record<string, unknown>)
      : undefined

  const coordinates =
    location && typeof location.coordinates === 'object' && location.coordinates !== null
      ? (location.coordinates as Record<string, unknown>)
      : undefined

  const lat = firstNumber([
    (data as { lat?: unknown }).lat,
    (data as { latitude?: unknown }).latitude,
    location?.lat,
    location?.latitude,
    coordinates?.lat,
    coordinates?.latitude,
    (locationRaw as { latitude?: unknown })?.latitude,
    (locationRaw as { _lat?: unknown })?._lat
  ])

  const lng = firstNumber([
    (data as { lng?: unknown }).lng,
    (data as { longitude?: unknown }).longitude,
    location?.lng,
    location?.longitude,
    coordinates?.lng,
    coordinates?.longitude,
    (locationRaw as { longitude?: unknown })?.longitude,
    (locationRaw as { _long?: unknown })?._long
  ])

  if (lat === null || lng === null) {
    return null
  }

  return { lat, lng }
}

function extractStageDurationMinutes(data: Record<string, unknown>): number | null {
  return firstNumber([
    (data as { duration?: unknown }).duration,
    (data as { durationMinutes?: unknown }).durationMinutes
  ])
}

function determineHighestRole(roles: string[], fallback?: string | null): UserRole {
  const normalized = roles.map(r => r.trim()).filter(Boolean)
  if (normalized.includes('superadmin')) return 'superadmin'
  if (normalized.includes('admin')) return 'admin'
  if (normalized.includes('moderator')) return 'moderator'
  if (fallback === 'superadmin' || fallback === 'admin' || fallback === 'moderator') return fallback
  return 'user'
}

function normalizeRoles(data: Record<string, unknown>): UserRole[] {
  const rolesValue = data.roles
  const roleValue = data.role
  const collected: string[] = []

  if (Array.isArray(rolesValue)) {
    for (const role of rolesValue) {
      if (isNonEmptyString(role)) {
        collected.push(role.trim())
      }
    }
  }

  if (isNonEmptyString(roleValue)) {
    collected.push(roleValue.trim())
  }

  if (collected.length === 0) {
    collected.push('user')
  }

  const highest = determineHighestRole(collected)
  const unique = Array.from(new Set(collected)) as UserRole[]
  if (!unique.includes(highest)) {
    unique.push(highest)
  }
  return unique
}

// Use shared normalize functions
const normalizeStatus = sharedNormalizeStatus
const normalizePlanInfo = sharedNormalizePlanInfo

function normalizeQuota(data: Record<string, unknown>) {
  const quota = (data.quota ?? data.quotas ?? {}) as Record<string, unknown>
  const current = (quota.current ?? {}) as Record<string, unknown>
  const limits = (quota.limits ?? {}) as Record<string, unknown>

  return {
    current: {
      plansGenerated: toNumber(current.plansGenerated, 0),
      aiRequests: toNumber(current.aiRequests, 0)
    },
    limits: {
      plansPerMonth: toNumber(limits.plansPerMonth, 20),
      aiRequestsPerDay: toNumber(limits.aiRequestsPerDay, 50)
    }
  }
}

async function countCollection(collectionPath: string, field: string, value: string): Promise<number> {
  const snapshot = await db.collection(collectionPath).where(field, '==', value).get()
  return snapshot.size
}

async function countUserShares(userId: string): Promise<number> {
  try {
    const shareLinksSnapshot = await db.collection('shareLinks').where('createdBy', '==', userId).get()
    if (!shareLinksSnapshot.empty) {
      return shareLinksSnapshot.size
    }
  } catch (error) {
    logger.warn('Failed to count shareLinks for user', { userId, error })
  }

  try {
    const legacySharesSnapshot = await db.collection('shares').where('userId', '==', userId).get()
    return legacySharesSnapshot.size
  } catch (error) {
    logger.warn('Failed to count legacy shares for user', { userId, error })
    return 0
  }
}

function buildSharingSummaryFromDocs(docs: FirestoreDoc[]): { total: number; public: number; private: number; pendingModeration: number } {
  if (!docs.length) {
    return { total: 0, public: 0, private: 0, pendingModeration: 0 }
  }

  let total = 0
  let publicCount = 0
  let privateCount = 0
  let pendingModeration = 0

  for (const doc of docs) {
    total += 1
    const data = doc.data() as Record<string, unknown>
    const privacy = typeof data.privacy === 'string' ? data.privacy.toLowerCase() : undefined
    const visibility = typeof data.visibility === 'string' ? data.visibility.toLowerCase() : undefined
    const isPublic =
      privacy === 'public' ||
      visibility === 'public' ||
      data.isPublic === true ||
      data.public === true

    if (isPublic) {
      publicCount += 1
    } else {
      privateCount += 1
    }

    const moderationStatus = typeof data.moderationStatus === 'string' ? data.moderationStatus.toLowerCase() : null
    if (moderationStatus === 'pending') {
      pendingModeration += 1
    }
  }

  return {
    total,
    public: publicCount,
    private: privateCount,
    pendingModeration
  }
}

async function calculateUserDistance(userId: string): Promise<number> {
  const plansSnapshot = await db.collection('plans').where('userId', '==', userId).get()
  let distanceKm = 0

  for (const planDoc of plansSnapshot.docs) {
    const planData = planDoc.data() as Plan
    if (planData?.routeData?.distance) {
      // routeData.distance is expected to be in kilometers.
      // Defensive: If value is suspiciously large (>10,000), assume it's in meters and convert to km.
      let routeDistance = Number(planData.routeData.distance) || 0
      if (routeDistance > 10000) {
        // Likely meters, convert to km
        routeDistance = routeDistance / 1000
      }
      distanceKm += routeDistance
      continue
    }

    const stagesSnapshot = await db
      .collection('stages')
      .where('planId', '==', planDoc.id)
      .orderBy('order', 'asc')
      .get()

    const stages = stagesSnapshot.docs.map(
      (stageDoc: FirestoreDoc) => stageDoc.data() as { location?: LatLng }
    )
    for (let index = 0; index < stages.length - 1; index++) {
      const current = stages[index]?.location
      const next = stages[index + 1]?.location
      if (!current || !next) continue
      // calculateDistance returns meters; convert to km
      distanceKm += calculateDistance(current, next) / 1000
    }
  }

  return Math.round(distanceKm * 100) / 100
}

/**
 * Build AdminUserListItem from Firestore document
 */
async function buildUserListItem(doc: FirestoreDoc): Promise<AdminUserListItem> {
  const data = doc.data()
  const userId = doc.id

  const [planCount, shareCount, flagsCount] = await Promise.all([
    countCollection('plans', 'userId', userId),
    countUserShares(userId),
    countCollection('flags', 'reporterId', userId)
  ])

  return {
    id: userId,
    email: typeof data.email === 'string' ? data.email : '',
    displayName: typeof data.displayName === 'string' ? data.displayName : '',
    roles: normalizeRoles(data),
    status: normalizeStatus(data.status),
    createdAt: toIsoDate(data.createdAt) ?? new Date().toISOString(),
    lastActive: toIsoDate(data.lastActive) ?? null,
    plan: normalizePlanInfo(data),
    quota: normalizeQuota(data),
    planCount,
    shareCount,
    flagsCount
  }
}

async function buildUserDetail(doc: FirestoreDoc): Promise<AdminUserDetail> {
  const base = await buildUserListItem(doc)
  const data = doc.data()

  const totalDistanceKm = await calculateUserDistance(doc.id)

  return {
    ...base,
    suspendedUntil: toIsoDate(data.suspendedUntil) ?? null,
    suspensionReason: typeof data.suspensionReason === 'string' && data.suspensionReason.trim().length > 0
      ? data.suspensionReason.trim()
      : null,
    subscriptionStatus: typeof data.subscriptionStatus === 'string' && data.subscriptionStatus.trim().length > 0
      ? data.subscriptionStatus.trim()
      : null,
    totalDistanceKm,
    achievementsCount: toNumber(data.achievementsCount, 0),
    stripeCustomerId: typeof data.stripeCustomerId === 'string' ? data.stripeCustomerId : null,
    stripeSubscriptionId: typeof data.stripeSubscriptionId === 'string' ? data.stripeSubscriptionId : null
  }
}


/* -------------------------------- repository ------------------------------- */

export class AdminRepository {
  private readonly usersCollection = db.collection('users')
  private readonly plansCollection = db.collection('plans')
  private readonly stagesCollection = db.collection('stages')
  private readonly shareLinksCollection = db.collection('shareLinks')
  private readonly sharesCollection = db.collection('shares')
  private readonly flagsCollection = db.collection('flags')
  private readonly auditCollection = db.collection('audit_logs')

  private async fetchSearchLogs(limit = 300): Promise<SearchLogEntry[]> {
    const results: SearchLogEntry[] = []
    const sources: Array<{ name: string; queryField: string | string[]; timestampField: string; tagsField?: string }> = [
      { name: 'search_history', queryField: 'query', timestampField: 'createdAt', tagsField: 'tags' },
      { name: 'search_logs', queryField: 'query', timestampField: 'timestamp', tagsField: 'tags' },
      { name: 'searchQueries', queryField: ['query', 'q'], timestampField: 'createdAt', tagsField: 'filters.tags' }
    ]

    for (const source of sources) {
      if (results.length >= limit) break
      const snapshot = await db
        .collection(source.name)
        .orderBy(source.timestampField, 'desc')
        .limit(limit)
        .get()
        .catch((error: unknown) => {
          logger.debug('Optional search log unavailable', { collection: source.name, error })
          return null
        })

      if (!snapshot) continue

      for (const doc of snapshot.docs as FirestoreDoc[]) {
        const data = doc.data() as Record<string, unknown>
        const queryFieldPaths = Array.isArray(source.queryField) ? source.queryField : [source.queryField]
        let queryValue: string | null = null

        for (const field of queryFieldPaths) {
          const candidate = getNestedValue(data, field)
          if (typeof candidate === 'string' && candidate.trim().length > 0) {
            queryValue = candidate
            break
          }
        }

        if (!queryValue) continue
        const normalizedQuery = queryValue.trim()
        if (!normalizedQuery) continue

        const createdAt =
          toIsoDate(getNestedValue(data, source.timestampField)) ?? new Date().toISOString()
        const tagsValue = source.tagsField ? getNestedValue(data, source.tagsField) : undefined
        const tags = extractStringArray(tagsValue ?? [])

        results.push({ query: normalizedQuery, createdAt, tags })
        if (results.length >= limit) break
      }
    }

    if (results.length < limit) {
      const analyticsEntries = await this.fetchAnalyticsSearchEvents(limit - results.length)
      results.push(...analyticsEntries)
    }

    return results
  }

  private async fetchAnalyticsSearchEvents(limit: number): Promise<SearchLogEntry[]> {
    if (limit <= 0) return []
    const snapshot = await db
      .collection('analytics_events')
      .orderBy('timestamp', 'desc')
      .limit(limit * 3)
      .get()
      .catch((error: unknown) => {
        logger.debug('Analytics events unavailable for search fallback', { error })
        return null
      })

    if (!snapshot) return []
    const entries: SearchLogEntry[] = []

    for (const doc of snapshot.docs as FirestoreDoc[]) {
      const data = doc.data() as Record<string, unknown>
      const eventName = typeof data.event === 'string' ? data.event.toLowerCase() : ''
      if (!eventName.includes('search')) {
        continue
      }

      const properties =
        typeof data.properties === 'object' && data.properties !== null
          ? (data.properties as Record<string, unknown>)
          : undefined
      let queryCandidate: string | null = null

      if (typeof properties?.query === 'string') {
        queryCandidate = properties.query
      } else if (typeof properties?.term === 'string') {
        queryCandidate = properties.term
      } else if (typeof data.query === 'string') {
        queryCandidate = data.query
      } else if (typeof data.term === 'string') {
        queryCandidate = data.term
      }

      if (!queryCandidate) continue
      const normalizedQuery = queryCandidate.trim()
      if (!normalizedQuery) continue

      const filters =
        typeof properties?.filters === 'object' && properties?.filters !== null
          ? (properties.filters as Record<string, unknown>)
          : undefined
      const tags = extractStringArray(properties?.tags ?? filters?.tags ?? [])

      const createdAt =
        toIsoDate(data.timestamp) ??
        toIsoDate(properties?.createdAt ?? filters?.createdAt) ??
        new Date().toISOString()

      entries.push({ query: normalizedQuery, createdAt, tags })
      if (entries.length >= limit) break
    }

    return entries
  }

  /**
   * Dashboard: merges features from both branches.
   * - Users: total, active(30d), newToday, byPlan
   * - Content: totalPlans, generatedToday, totalStops, totalDistance
   * - Moderation: openFlags, resolvedToday, averageResponseTime (minutes)
   * - Performance: simple metrics incl. avgResponseTime (this call), uptime
   * - Financial: placeholders (0) – keep shape, fill from billing later
   */
  async getDashboardStats(): Promise<AdminDashboardStats> {
    const t0 = Date.now()

    const [usersSnapshot, plansSnapshot, stagesSnapshot] = await Promise.all([
      this.usersCollection.get(),
      this.plansCollection.get(),
      this.stagesCollection.get()
    ])

    const flagsSnapshot: FirestoreSnapshot | null = await this.flagsCollection.get().catch((error: unknown) => {
      logger.warn('Failed to fetch flags collection for dashboard stats', { error })
      return null
    })

    const shareLinksSnapshot: FirestoreSnapshot | null = await this.shareLinksCollection
      .get()
      .catch((error: unknown) => {
        logger.warn('Failed to fetch shareLinks collection for dashboard stats', { error })
        return null
      })

    const legacySharesSnapshot: FirestoreSnapshot | null = !shareLinksSnapshot || shareLinksSnapshot.empty
      ? await this.sharesCollection.get().catch((error: unknown) => {
        logger.warn('Failed to fetch legacy shares collection for dashboard stats', { error })
        return null
      })
      : null

    const now = Date.now()
    const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000
    const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000
    const startOfDay = (() => {
      const d = new Date()
      d.setHours(0, 0, 0, 0)
      return d.getTime()
    })()
    const keywordCollector = new KeywordCollector()
    const planTagCounts: Map<string, number> = new Map()
    const poiTracker = new PoiStatsTracker()
    let plansWithKeywordSignal = 0
    const searchLogs = await this.fetchSearchLogs(400)

    // Users
    let activeUsers = 0
    let newUsersToday = 0
    const planDistribution: Record<'free' | 'premium' | 'pro', number> = { free: 0, premium: 0, pro: 0 }
    const gamificationPlayers: AdminGamificationPlayerSummary[] = []
    const badgeCounts = new Map<string, AdminGamificationBadgeSummary>()
    let gamificationPlayerCount = 0
    let gamificationActiveThisWeek = 0
    let gamificationTotalPoints = 0
    let gamificationLevelSum = 0

    for (const doc of usersSnapshot.docs) {
      const data = doc.data()
      const lastActiveIso = toIsoDate(data.lastActive ?? data.lastLoginAt)
      if (lastActiveIso && new Date(lastActiveIso).getTime() >= thirtyDaysAgo) {
        activeUsers += 1
      }

      const createdAtIso = toIsoDate(data.createdAt)
      if (createdAtIso && new Date(createdAtIso).getTime() >= startOfDay) {
        newUsersToday += 1
      }

      const planInfo = normalizePlanInfo(data)
      planDistribution[planInfo.tier] += 1

      const gamification = (data.gamification ?? {}) as Record<string, unknown>
      const points = toNumber((gamification.points as number | undefined) ?? (gamification as any)?.score, 0)
      const level = toNumber(gamification.level, 1)
      const badgesRaw = Array.isArray((gamification as any)?.badges) ? ((gamification as any).badges as unknown[]) : []
      const hasGamification = points > 0 || level > 1 || badgesRaw.length > 0

      if (hasGamification) {
        gamificationPlayerCount += 1
        gamificationTotalPoints += points
        gamificationLevelSum += level

        const gamificationUpdatedIso =
          toIsoDate((gamification as any)?.updatedAt ?? data.lastActive ?? data.lastLoginAt) ?? null
        const gamificationUpdatedAt = gamificationUpdatedIso ? new Date(gamificationUpdatedIso).getTime() : null
        if (gamificationUpdatedAt && gamificationUpdatedAt >= sevenDaysAgo) {
          gamificationActiveThisWeek += 1
        }

        const displayName =
          typeof data.displayName === 'string' && data.displayName.trim().length > 0
            ? data.displayName
            : typeof data.email === 'string'
              ? data.email
              : `Utilisateur ${doc.id.slice(0, 6)}`

        gamificationPlayers.push({
          id: doc.id,
          displayName,
          points,
          level,
          badges: badgesRaw.length,
          lastActive: toIsoDate(data.lastActive ?? data.lastLoginAt)
        })

        for (const badge of badgesRaw) {
          const badgeId = typeof (badge as any)?.id === 'string' ? ((badge as any).id as string) : null
          if (!badgeId) continue
          const label =
            typeof (badge as any)?.name === 'string' && (badge as any).name.trim().length > 0
              ? ((badge as any).name as string)
              : badgeId
          const icon = typeof (badge as any)?.icon === 'string' ? ((badge as any).icon as string) : null

          const existing = badgeCounts.get(badgeId)
          if (existing) {
            existing.count += 1
          } else {
            badgeCounts.set(badgeId, { badgeId, label, icon, count: 1 })
          }
        }
      }
    }

    // Content
    const stagesByPlan = new Map<string, Array<{ order: number; coordinates: LatLng | null; durationMinutes: number | null }>>()
    for (const stageDoc of stagesSnapshot.docs as FirestoreDoc[]) {
      const data = stageDoc.data() as Record<string, unknown>
      let planId: string | null = null

      if (typeof data.planId === 'string' && data.planId.trim().length > 0) {
        planId = data.planId.trim()
      } else if (
        data.planId &&
        typeof data.planId === 'object' &&
        typeof (data.planId as { id?: unknown }).id === 'string'
      ) {
        planId = (data.planId as { id: string }).id
      } else if (typeof (data as { planID?: unknown }).planID === 'string') {
        planId = ((data as { planID: string }).planID).trim()
      }

      if (!planId) {
        continue
      }

      collectStageKeywords(keywordCollector, data)

      const order = coerceNumber((data as { order?: unknown }).order) ?? 0
      const coordinates = extractStageCoordinates(data)
      const durationMinutes = extractStageDurationMinutes(data)

      const stages = stagesByPlan.get(planId) ?? []
      stages.push({ order, coordinates, durationMinutes })
      stagesByPlan.set(planId, stages)

      const stageTimestamp = toIsoDate(data.updatedAt ?? data.createdAt)
      const stagePoisRaw =
        (data as { pois?: unknown }).pois ?? (data as { poiData?: unknown }).poiData
      const stagePois = extractPois(stagePoisRaw, stageTimestamp)
      for (const poi of stagePois) {
        poiTracker.add(planId, poi, startOfDay)
      }
    }

    let generatedToday = 0
    let totalDistanceMeters = 0

    for (const doc of plansSnapshot.docs) {
      const data = doc.data() as Record<string, unknown>
      const createdAt = toIsoDate(data.createdAt)
      if (createdAt && new Date(createdAt).getTime() >= startOfDay) {
        generatedToday += 1
      }
      if (collectPlanKeywords(keywordCollector, planTagCounts, data)) {
        plansWithKeywordSignal += 1
      }

      const fallbackTimestamp = toIsoDate(data.updatedAt ?? data.createdAt)
      const planPoisRaw =
        (data as { pois?: unknown }).pois ?? (data as { poiData?: unknown }).poiData
      const planPois = extractPois(planPoisRaw, fallbackTimestamp)
      for (const poi of planPois) {
        poiTracker.add(doc.id, poi, startOfDay)
      }

      const planStats = (data?.stats ?? {}) as Record<string, unknown>
      const aggregatedDistanceMeters = coerceNumber((planStats as { totalDistance?: unknown }).totalDistance)
      let hasAggregatedDistance = false
      if (aggregatedDistanceMeters !== null && aggregatedDistanceMeters >= 0) {
        totalDistanceMeters += aggregatedDistanceMeters
        hasAggregatedDistance = true
      } else {
        const rawRouteDistance = coerceNumber((data?.routeData as { distance?: unknown } | undefined)?.distance)
        if (rawRouteDistance !== null && rawRouteDistance >= 0) {
          const normalizedKm = rawRouteDistance > 10000 ? rawRouteDistance / 1000 : rawRouteDistance
          totalDistanceMeters += normalizedKm * 1000
          hasAggregatedDistance = true
        }
      }

      if (!hasAggregatedDistance) {
        const planStages = stagesByPlan.get(doc.id)
        if (planStages && planStages.length > 1) {
          const orderedCoordinates = planStages
            .filter(stage => stage.coordinates)
            .sort((a, b) => a.order - b.order) as Array<{ order: number; coordinates: LatLng; durationMinutes: number | null }>

          for (let index = 0; index < orderedCoordinates.length - 1; index++) {
            const current = orderedCoordinates[index].coordinates
            const next = orderedCoordinates[index + 1].coordinates
            totalDistanceMeters += calculateDistance(current, next)
          }
        }
      }
    }

    // Moderation
    const flagsDocs: FirestoreDoc[] = (flagsSnapshot?.docs ?? []) as FirestoreDoc[]
    const openFlags = flagsDocs.filter((flagDoc) => (flagDoc.data()?.status ?? 'open') === 'open').length
    const resolvedToday = flagsDocs.filter((flagDoc) => {
      const resolvedAt = toIsoDate(flagDoc.data().resolvedAt)
      return resolvedAt ? new Date(resolvedAt).getTime() >= startOfDay : false
    }).length

    const responseTimesMs: number[] = []
    for (const flag of flagsDocs) {
      const createdAt = toIsoDate(flag.data().createdAt)
      const resolvedAt = toIsoDate(flag.data().resolvedAt)
      if (!createdAt || !resolvedAt) continue
      responseTimesMs.push(new Date(resolvedAt).getTime() - new Date(createdAt).getTime())
    }
    const averageResponseTime =
      responseTimesMs.length
        ? Math.round(responseTimesMs.reduce((s, v) => s + v, 0) / responseTimesMs.length / 60000)
        : 0

    const shareDocs: FirestoreDoc[] = (
      (shareLinksSnapshot && !shareLinksSnapshot.empty
        ? shareLinksSnapshot.docs
        : legacySharesSnapshot?.docs ?? []) as FirestoreDoc[]
    )
    const sharingSummary = buildSharingSummaryFromDocs(shareDocs)

    // Lightweight performance metrics (no external deps)
    const apiLatencyMs = Date.now() - t0

    const averageLevel =
      gamificationPlayerCount > 0 ? Math.round((gamificationLevelSum / gamificationPlayerCount) * 10) / 10 : 0
    const topPlayers = gamificationPlayers
      .slice()
      .sort((a, b) => {
        if (b.points === a.points) {
          return b.level - a.level
        }
        return b.points - a.points
      })
      .slice(0, 5)
    const topBadges = Array.from(badgeCounts.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 5)

    const gamificationSummary: AdminGamificationSummary = {
      totalPlayers: gamificationPlayerCount,
      activeThisWeek: gamificationActiveThisWeek,
      totalPoints: Math.round(gamificationTotalPoints),
      averageLevel,
      topPlayers,
      topBadges
    }

    const keywordInsights = {
      totalTokens: keywordCollector.totalTokens,
      uniqueTerms: keywordCollector.uniqueCount(),
      coverage:
        plansSnapshot.size > 0
          ? Math.min(100, Math.round((plansWithKeywordSignal / plansSnapshot.size) * 100))
          : 0,
      topWords: keywordCollector.top(8)
    }

    const searchQueryCounts: Map<string, number> = new Map()
    const searchTagCounts: Map<string, number> = new Map()
    let lastQueryAt: string | null = null
    for (const entry of searchLogs) {
      searchQueryCounts.set(entry.query, (searchQueryCounts.get(entry.query) ?? 0) + 1)
      if (!lastQueryAt || entry.createdAt > lastQueryAt) {
        lastQueryAt = entry.createdAt
      }
      for (const tag of entry.tags) {
        searchTagCounts.set(tag, (searchTagCounts.get(tag) ?? 0) + 1)
      }
    }

    if (searchTagCounts.size < 3 && planTagCounts.size) {
      for (const [tag, count] of planTagCounts.entries()) {
        searchTagCounts.set(tag, (searchTagCounts.get(tag) ?? 0) + count)
      }
    }

    const planTagsTotal = Array.from(planTagCounts.values()).reduce((sum, value) => sum + value, 0)
    const searchTotals = searchLogs.length
    const queriesToday = searchLogs.filter((entry) => new Date(entry.createdAt).getTime() >= startOfDay).length
    const uniqueQueries = new Set(searchLogs.map(entry => entry.query.toLowerCase())).size

    const searchInsights = {
      total: searchTotals,
      queriesToday,
      uniqueQueries,
      lastQueryAt,
      topQueries: mapToStats(searchQueryCounts, searchTotals, 6),
      topTags: mapToStats(
        searchTagCounts,
        Math.max(searchTotals, planTagsTotal, 1),
        6
      )
    }

    const poiInsights = {
      totalSaved: poiTracker.total,
      savedToday: poiTracker.savedToday,
      avgPerPlan: poiTracker.averagePerPlan(plansSnapshot.size),
      topCategories: mapToStats(poiTracker.categories, Math.max(poiTracker.total, 1), 5),
      topCities: mapToStats(poiTracker.cities, Math.max(poiTracker.total, 1), 5)
    }

    const maxBox = {
      title: 'MAXBOX · Data Pulse',
      subtitle: `${keywordInsights.uniqueTerms.toLocaleString()} mots-clés trackés`,
      metrics: [
        {
          label: 'Recherches (24h)',
          value: searchInsights.queriesToday,
          unit: '',
          trend: searchInsights.total > 0 ? Math.round((searchInsights.queriesToday / searchInsights.total) * 100) : 0,
          description: `${searchInsights.total.toLocaleString()} requêtes sur 30j`,
          icon: 'travel_explore'
        },
        {
          label: 'POI enregistrés',
          value: poiInsights.totalSaved,
          unit: '',
          trend:
            poiInsights.totalSaved > 0
              ? Math.round((poiInsights.savedToday / Math.max(poiInsights.totalSaved, 1)) * 100)
              : 0,
          description: `${poiInsights.savedToday} ajouts aujourd'hui`,
          icon: 'place'
        },
        {
          label: 'Tokens captés',
          value: keywordInsights.totalTokens,
          unit: '',
          trend: keywordInsights.coverage,
          description: `${keywordInsights.coverage}% des plans couverts`,
          icon: 'article'
        }
      ],
      segments: [
        {
          label: 'Mot dominant',
          value: keywordInsights.topWords[0]?.label ?? '—',
          hint: `${keywordInsights.topWords[0]?.count ?? 0} occurrences`
        },
        {
          label: 'Requête #1',
          value: searchInsights.topQueries[0]?.label ?? '—',
          hint: `${searchInsights.topQueries[0]?.count ?? 0} recherches`
        },
        {
          label: 'Catégorie POI',
          value: poiInsights.topCategories[0]?.label ?? '—',
          hint: `${poiInsights.topCategories[0]?.count ?? 0} POI`
        }
      ]
    }

    const dataIntelligence = {
      keywords: keywordInsights,
      searches: searchInsights,
      pois: poiInsights,
      maxBox
    }

    return {
      users: {
        total: usersSnapshot.size,
        active: activeUsers,
        newToday: newUsersToday,
        byPlan: planDistribution
      },
      content: {
        totalPlans: plansSnapshot.size,
        generatedToday,
        totalStops: stagesSnapshot.size,
        totalDistance: Math.round(totalDistanceMeters / 1000)
      },
      moderation: {
        openFlags,
        resolvedToday,
        averageResponseTime
      },
      performance: {
        cacheHitRate: 0,              // TODO: wire real cache
        avgResponseTime: apiLatencyMs,
        errorRate: 0,                 // TODO: feed from error tracker
        uptime: Math.round(process.uptime())
      },
      financial: {
        mrr: 0,                       // TODO: Stripe/Billing
        totalRevenue: 0,              // TODO: Stripe/Billing
        conversionRate: 0             // TODO: funnel metrics
      },
      sharing: sharingSummary,
      gamification: gamificationSummary,
      dataIntelligence
    }
  }

  /**
   * Cursor pagination + optional status/search filters.
   * Keeps features from Codex branch & resolves conflicts with main.
   */
  async listUsers(params: ListUsersQuery): Promise<{ users: AdminUserListItem[]; total: number; nextCursor?: string | null }> {
    const baseLimit = params.limit ?? 50
    const fetchLimit = params.search ? Math.min(500, baseLimit * 5) : baseLimit
    const cursor = params.cursor
    const search = params.search?.trim().toLowerCase() || ''
    const status = params.status && params.status !== 'all' ? params.status : null

    let query = this.usersCollection
      .orderBy('createdAt', 'desc')
      .limit(fetchLimit) as FirestoreQuery

    if (status) {
      query = query.where('status', '==', status) as FirestoreQuery
    }

    if (!search && cursor) {
      const cursorDoc = await this.usersCollection.doc(cursor).get()
      if (cursorDoc.exists) {
        query = query.startAfter(cursorDoc) as FirestoreQuery
      }
    }

    const snapshot = await query.get()
    let docs: FirestoreDoc[] = snapshot.docs as FirestoreDoc[]

    if (search) {
      docs = docs.filter((doc) => {
        const data = doc.data()
        const email = typeof data.email === 'string' ? data.email.toLowerCase() : ''
        const displayName = typeof data.displayName === 'string' ? data.displayName.toLowerCase() : ''
        return email.includes(search) || displayName.includes(search)
      })
    }

    const limitedDocs = docs.slice(0, baseLimit)

    const users: AdminUserListItem[] = []
    for (const doc of limitedDocs) {
      users.push(await buildUserListItem(doc as FirestoreDoc))
    }

    const totalSnapshot = status
      ? await this.usersCollection.where('status', '==', status).get()
      : await this.usersCollection.get()

    const nextCursor =
      search || limitedDocs.length < baseLimit
        ? null
        : limitedDocs.at(-1)?.id ?? null

    return {
      users,
      total: totalSnapshot.size,
      nextCursor
    }
  }

  async getUserById(userId: string): Promise<AdminUserDetail | null> {
    const doc = await this.usersCollection.doc(userId).get()
    if (!doc.exists) return null
    return await buildUserDetail(doc as FirestoreDoc)
  }

  async updateUserStatus(
    userId: string,
    status: UserStatus,
    extra?: { suspendedUntil?: string | null; suspensionReason?: string | null }
  ): Promise<void> {
    const updates: Record<string, unknown> = {
      status,
      updatedAt: new Date().toISOString()
    }

    if (extra) {
      updates.suspendedUntil = extra.suspendedUntil ?? null
      updates.suspensionReason = extra.suspensionReason ?? null
    }

    await this.usersCollection.doc(userId).set(updates, { merge: true })
  }

  async updateUserRoles(userId: string, roles: UserRole[]): Promise<void> {
    const uniqueRoles = Array.from(new Set(roles))
    const highestRole = determineHighestRole(uniqueRoles)
    await this.usersCollection.doc(userId).set(
      {
        roles: uniqueRoles,
        role: highestRole,
        admin: highestRole === 'admin' || highestRole === 'superadmin',
        updatedAt: new Date().toISOString()
      },
      { merge: true }
    )
  }

  async changeUserPlan(userId: string, plan: AdminUserPlanInfo, subscriptionStatus?: string | null): Promise<void> {
    await this.usersCollection.doc(userId).set(
      {
        planId: plan.id ?? null,
        planName: plan.name,
        planTier: plan.tier,
        plan: plan.tier,
        subscriptionStatus: subscriptionStatus ?? null,
        planUpdatedAt: new Date().toISOString()
      },
      { merge: true }
    )
  }

  async resetUserQuota(userId: string): Promise<void> {
    await this.usersCollection.doc(userId).set(
      {
        quota: {
          current: {
            plansGenerated: 0,
            aiRequests: 0
          }
        },
        quotaResetAt: new Date().toISOString()
      },
      { merge: true }
    )
  }

  async getModerationFlags(): Promise<ModerationFlag[]> {
    const snapshot = await this.flagsCollection.where('status', '==', 'open').orderBy('createdAt', 'asc').get()

    return snapshot.docs.map((doc: FirestoreDoc) => {
      const data = doc.data()
      return {
        id: doc.id,
        type: (data.type ?? 'plan') as ModerationFlag['type'],
        itemId: typeof data.itemId === 'string' ? data.itemId : doc.id,
        clanId: typeof data.clanId === 'string' ? data.clanId : undefined,
        reporterId: typeof data.reporterId === 'string' ? data.reporterId : 'unknown',
        reason: typeof data.reason === 'string' ? data.reason : 'Signalement',
        createdAt: toIsoDate(data.createdAt) ?? new Date().toISOString(),
        status: (data.status ?? 'open') as ModerationFlag['status'],
        resolvedBy: typeof data.resolvedBy === 'string' ? data.resolvedBy : undefined,
        resolution: typeof data.resolution === 'string' ? data.resolution : undefined,
        resolvedAt: toIsoDate(data.resolvedAt) ?? undefined,
        reportedUserId: typeof data.reportedUserId === 'string' ? data.reportedUserId : undefined,
        content: data.content && typeof data.content === 'object'
          ? (data.content as ModerationFlag['content'])
          : undefined
      }
    })
  }

  async getModerationStats(): Promise<ModerationStats> {
    const [openSnapshot, resolvedSnapshot, dismissedSnapshot] = await Promise.all([
      this.flagsCollection.where('status', '==', 'open').get(),
      this.flagsCollection.where('status', '==', 'resolved').get(),
      this.flagsCollection.where('status', '==', 'dismissed').get()
    ])

    const startOfDay = (() => {
      const date = new Date()
      date.setHours(0, 0, 0, 0)
      return date.getTime()
    })()

    const resolvedToday = resolvedSnapshot.docs.filter((doc: FirestoreDoc) => {
      const resolvedAt = toIsoDate(doc.data().resolvedAt)
      return resolvedAt ? new Date(resolvedAt).getTime() >= startOfDay : false
    }).length

    const dismissedToday = dismissedSnapshot.docs.filter((doc: FirestoreDoc) => {
      const resolvedAt = toIsoDate(doc.data().resolvedAt)
      return resolvedAt ? new Date(resolvedAt).getTime() >= startOfDay : false
    }).length

    return {
      open: openSnapshot.size,
      resolvedToday,
      dismissedToday
    }
  }

  async resolveFlag(
    flagId: string,
    updates: { status: ModerationFlag['status']; resolution?: string; resolvedBy: string }
  ): Promise<ModerationFlag | null> {
    const docRef = this.flagsCollection.doc(flagId)
    const doc = await docRef.get()
    if (!doc.exists) return null

    const payload = {
      status: updates.status,
      resolution: updates.resolution ?? null,
      resolvedBy: updates.resolvedBy,
      resolvedAt: new Date().toISOString()
    }

    await docRef.set(payload, { merge: true })

    const updated = await docRef.get()
    // Try to reuse mapped version if present
    const flags = await this.getModerationFlags()
    const match = flags.find(flag => flag.id === updated.id)
    if (match) return match

    const data = updated.data() ?? {}
    return {
      id: updated.id,
      type: (data.type ?? 'plan') as ModerationFlag['type'],
      itemId: typeof data.itemId === 'string' ? data.itemId : updated.id,
      clanId: typeof data.clanId === 'string' ? data.clanId : undefined,
      reporterId: typeof data.reporterId === 'string' ? data.reporterId : 'unknown',
      reason: typeof data.reason === 'string' ? data.reason : 'Signalement',
      createdAt: toIsoDate(data.createdAt) ?? new Date().toISOString(),
      status: updates.status,
      resolvedBy: updates.resolvedBy,
      resolution: updates.resolution ?? undefined,
      resolvedAt: toIsoDate(data.resolvedAt) ?? new Date().toISOString(),
      reportedUserId: typeof data.reportedUserId === 'string' ? data.reportedUserId : undefined,
      content: data.content && typeof data.content === 'object'
        ? (data.content as ModerationFlag['content'])
        : undefined
    }
  }

  async logAudit(entry: Omit<AuditLogEntry, 'id' | 'timestamp'> & { timestamp?: string }): Promise<AuditLogEntry> {
    const timestamp = entry.timestamp ?? new Date().toISOString()
    const docRef = this.auditCollection.doc()
    const payload = { ...entry, timestamp, id: docRef.id }
    await docRef.set(payload)
    return payload
  }

  async getAuditLogs(limit = 100): Promise<AuditLogEntry[]> {
    const snapshot = await this.auditCollection.orderBy('timestamp', 'desc').limit(limit).get()
    return snapshot.docs.map((doc: FirestoreDoc) => doc.data() as AuditLogEntry)
  }
}
